import {
  ChangeEvent, forwardRef, memo, useCallback,
} from 'react';
import { Pagination as MUIPagination, PaginationProps } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { type TFunction } from 'i18next';
import { IComponentWithCustomScrollbarProps } from '../../services';
import Grid from '../Grid';
import MenuItem from '../MenuItem';
import Select from '../Select';
import Typography from '../Typography';
import {
  AB3, S1, S2, S4, S6,
} from '../../theme';
import { PageSizeOptionEnum } from '../../enums';
import { mergeSX } from '../../services/helpers';

const buildSX = () => ({
  '& .MuiPagination-ul': {
    columnGap: '8px',
  },
  '& .MuiPaginationItem-root': {
    borderRadius: '50%',
    margin: 0,
    color: S6,
    '&:hover': {
      backgroundColor: S2,
      color: AB3,
    },
    '&.Mui-selected': {
      backgroundColor: AB3,
      color: S1,
      '&:hover': {
        backgroundColor: S2,
        color: AB3,
      },
    },
    '&.Mui-disabled': {
      color: S4,
    },
  },
});

const PAGE_SIZE_OPTIONS = Object.values(PageSizeOptionEnum).filter((
  option,
) => typeof option === 'number');

export interface IPaginationProps extends PaginationProps, IComponentWithCustomScrollbarProps {
  t?: TFunction<'translation', undefined>,
  perPage: number,
  onPerPageChange: (e: SelectChangeEvent<unknown>) => void,
  activePage: number,
  onActivePageChange: (value: number) => void,
  count: number;
  totalItems: number;
}

const TD_STYLE = { padding: 0 };

const Pagination = forwardRef<HTMLTableCellElement, IPaginationProps>((props, ref) => {
  const {
    t,
    perPage,
    onPerPageChange,
    activePage,
    onActivePageChange,
    count,
    totalItems,
    disableCustomScrollbar = false,
    sx,
    ...restProps
  } = props;

  const handlePerPageChange = useCallback((
    e: SelectChangeEvent<unknown>,
  ) => onPerPageChange(e), [onPerPageChange]);
  const handleActivePageChange = useCallback((
    e: ChangeEvent<unknown>,
    page: number,
  ) => onActivePageChange(page), [onActivePageChange]);
  if (!totalItems) {
    return null;
  }
  const labelDisplayedRowsFrom = ((activePage - 1) * perPage + 1);
  const labelDisplayedRowsTo = (Math.min(totalItems, activePage * perPage));

  return (
    <td ref={ref} style={TD_STYLE}>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        /*
         rowGap - space between pagination and rowsPerPage selector; for the case when they cannot
         fit into one line since the table width is too small -> they will stack on top of
         each other
        */
        rowGap={2}
        columnGap={4.5}
        p={1}
      >
        <Grid item>
          <MUIPagination
            {...restProps}
            boundaryCount={1}
            count={count}
            page={activePage}
            onChange={handleActivePageChange}
            sx={mergeSX(buildSX(), sx)}
          />
        </Grid>
        <Grid item>
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              <Typography>{t ? t('table.rowsPerPage') : 'Rows per page'}</Typography>
            </Grid>
            <Grid item>
              <Select
                disableMinWidth
                value={perPage}
                onChange={handlePerPageChange}
                disableCustomScrollbar={disableCustomScrollbar}
              >
                {PAGE_SIZE_OPTIONS.map((el) => <MenuItem key={el} value={el}>{el}</MenuItem>)}
              </Select>
            </Grid>
            <Grid item>
              <Typography>
                {`${labelDisplayedRowsFrom}-${labelDisplayedRowsTo} ${
                  t ? t('table.of') : 'of'} ${totalItems}`}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </td>
  );
});

export default memo(Pagination);
